import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule, provideZoneChangeDetection } from '@angular/core';

import { AppComponent } from './app.component';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { OAuthModule } from 'angular-oauth2-oidc';
import { LogoutComponent } from './pages/logout/logout.component';
import { HomeComponent } from './pages/home/home.component';
import { CustomComponentsModule } from './modules/custom-components/custom-components.module';
import { ConfigurationService, LanguageService, LightformsServicesModule } from '@un-fao/lightforms-lib/lightforms-services';
import { LightformsComponentsModule } from '@un-fao/lightforms-lib/lightforms-components';
import { LightformsCoreModule } from '@un-fao/lightforms-lib/lightforms-core';
import { PrimengModule } from './primeng.module';
import { register as registerSwiperElements } from 'swiper/element/bundle';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

registerSwiperElements();

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, environment.translations.prefix, environment.translations.suffix);
}

export function appInitializerFactory(translate: TranslateService, languages: LanguageService) {
  return () => {
    languages.load();
    translate.use('en');
    return translate.get('common.main.title').toPromise();
  };
}

const MyPreset = definePreset(Aura, {
    semantic: {
        colorScheme: {
            light: {
                primary: {
                    color: '#0d6cac',
                    inverseColor: '#ffffff',
                    hoverColor: '#0069d9',
                    activeColor: '#0d6cac'
                },
                highlight: {
                    background: '{blue.950}',
                    focusBackground: '{blue.700}',
                    color: '#ffffff',
                    focusColor: '#ffffff'
                }
            },
            dark: {
                primary: {
                    color: '{blue.50}',
                    inverseColor: '{blue.950}',
                    hoverColor: '{blue.100}',
                    activeColor: '{blue.200}'
                },
                highlight: {
                    background: 'rgba(250, 250, 250, .16)',
                    focusBackground: 'rgba(250, 250, 250, .24)',
                    color: 'rgba(255,255,255,.87)',
                    focusColor: 'rgba(255,255,255,.87)'
                }
            }
        }
    },
    components: {
        paginator: {
            colorScheme: {
                navButton: {
                    background: "transparent",
                    hoverBackground: "{content.hover.background}",
                    selectedBackground: "#0d6cac",
                    color: "{text.muted.color}",
                    hoverColor: "{text.hover.muted.color}",
                    selectedColor: "{highlight.color}",
                    width: "2.5rem",
                    height: "2.5rem",
                    borderRadius: "50%",
                    focusRing: {
                      width: "{focus.ring.width}",
                      style: "{focus.ring.style}",
                      color: "{focus.ring.color}",
                      offset: "{focus.ring.offset}",
                      shadow: "{focus.ring.shadow}"
                    }
                  },
            }
        },
        button: {
            colorScheme: {
                light: {
                  root: {
                    warn: {
                        background: "#ffc107",
                        hoverBackground: "{yellow.600}",
                        activeBackground: "{yellow.700}",
                        borderColor: "#ffc107",
                        hoverBorderColor: "{yellow.600}",
                        activeBorderColor: "{yellow.700}",
                        color: "#000",
                        hoverColor: "#000",
                        activeColor: "#000",
                        focusRing: {
                          color: "#ffc107",
                          shadow: "none"
                        }
                      }
                    },
                    info: {
                        background: "#17a2b8",
                        hoverBackground: "{sky.600}",
                        activeBackground: "{sky.700}",
                        borderColor: "#17a2b8",
                        hoverBorderColor: "{sky.600}",
                        activeBorderColor: "{sky.700}",
                        color: "#ffffff",
                        hoverColor: "#ffffff",
                        activeColor: "#ffffff",
                        focusRing: {
                          color: "#17a2b8",
                          shadow: "none"
                        }
                    }
                },
                outlined: {
                    warn: {
                        hoverBackground: "{yellow.50}",
                        activeBackground: "{yellow.100}",
                        borderColor: "{yellow.200}",
                        color: "#ffc107"
                    },
                    info: {
                        hoverBackground: "{sky.50}",
                        activeBackground: "{sky.100}",
                        borderColor: "{sky.200}",
                        color: "#17a2b8"
                    }
                },
                text: {
                    warn: {
                        hoverBackground: "{yellow.50}",
                        activeBackground: "{yellow.100}",
                        color: "#ffc107"
                    },
                    info: {
                      hoverBackground: "{sky.50}",
                      activeBackground: "{sky.100}",
                      color: "#17a2b8"
                    }
                }
            }
        },
        accordion: {
            colorScheme: {
                light: {
                    header: {
                        color: "{text.muted.color}",
                        hoverColor: "{text.color}",
                        activeColor: "{text.color}",
                        padding: "0.8rem",
                        fontWeight: "600",
                        borderWidth: "1px",
                        borderColor: "#dee2e6",
                        background: "#f8f9fa",
                        hoverBackground: "#f8f9fa",
                        activeBackground: "#f8f9fa",
                        activeHoverBackground: "#f8f9fa"
                    },
                    panel: {
                        borderWidth: "1px",
                        borderColor: "#dee2e6"
                    },
                    content: {
                        borderWidth: "0",
                        borderColor: "#dee2e6",
                        background: "{content.background}",
                        color: "{text.color}",
                        padding: "0 1.125rem 1.125rem 1.125rem"
                    },
                    first: {
                        topBorderRadius: "{content.border.radius}",
                        borderWidth: "0"
                    },
                    last: {
                        bottomBorderRadius: "{content.border.radius}",
                        activeBottomBorderRadius: "0"
                    }
                }
            }
        },
        panel: {
            colorScheme: {
                light: {
                    header: {
                        background: "#f8f9fa",
                        color: "{text.color}",
                        padding: "1.125rem",
                        borderColor: "{content.border.color}",
                        borderWidth: "0",
                        borderRadius: "0"
                    },
                    content: {
                        padding: "1.125rem 1.125rem 1.125rem 1.125rem"
                    }
                }
            }
        }
    },
    css: ({ dt }) => `
        /* Global CSS */
        
       
    `
  });

@NgModule({ declarations: [
        AppComponent,
        LogoutComponent,
        HomeComponent
    ],
    exports: [
        PrimengModule,
        AppRoutingModule,
        CustomComponentsModule
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        PrimengModule,
        NoopAnimationsModule,
        CustomComponentsModule,
        TranslateModule.forRoot({
            defaultLanguage: 'en',
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            },
            isolate: true
        }),
        LightformsServicesModule,
        LightformsComponentsModule,
        LightformsCoreModule.forRoot({
            servicesUrl: environment.servicesUrl,
            securityUrl: environment.securityUrl,
            siteUrl: environment.siteUrl,
            storageUrl: environment.storageUrl,
            enabledLanguages: environment.enabledLanguages,
            space: environment.space,
            statuses: environment.statuses,
            reviewStatuses: environment.reviewStatuses,
            status: environment.status,
            token: environment.token,
            modules: [
                CustomComponentsModule
            ],
            header: {
                enableLogin: false
            },
            configurationsFile: "./assets/config/endpoints.json",
            hosted: true,
            currentLang: 'en',
            defaultLang: 'en',
            i18n: {
                en: {}
            }
        }),
        AppRoutingModule,
        OAuthModule.forRoot({
            resourceServer: {
                allowedUrls: [environment.servicesUrl],
                sendAccessToken: true
            }
        })], providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [TranslateService, LanguageService],
            multi: true
        },
        {
            provide: APP_BASE_HREF,
            useFactory: (s: PlatformLocation) => environment.siteUrl,
            deps: [PlatformLocation]
        },
        ConfigurationService,
        provideHttpClient(withInterceptorsFromDi()),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: MyPreset
            }
        })
    ] })
export class AppModule { 

  ngDoBootstrap() { }
}
